import { format } from 'date-fns';
import { addStoreFilterToParams, StoreFilterOptions } from '../hooks/useStoreFilter';
import { ENDPOINTS } from '../config';
import { apiRequest, clearApiCache } from './apiClient';

interface RequestOptions {
  method?: "GET" | "POST" | "PUT" | "DELETE" | "PATCH";
  headers?: Record<string, string>;
  body?: any;
  requiresAuth?: boolean;
  isFormData?: boolean;
  isMultipartFormData?: boolean;
}

// Define interfaces for Quote-related data
export interface QuoteItem {
  id?: string;
  quote_id?: string;
  // Frontend form fields
  sku: string;
  description: string;
  units: number;
  boxes: number;
  pieces: number;
  m2: number;
  blm: number;  // BLM field for carpet measurements
  unit_price: number;
  total_price?: number;
  // Backend API field names (optional as they're mapped during API calls)
  sku_code?: string;
  quantity_units?: number;
  quantity_boxes?: number;
  quantity_pieces?: number;
  quantity_m2?: number;
  quantity_blm?: number;
  created_at?: string;
  updated_at?: string;
}

// Interface for creating a quote item for the API
export interface QuoteItemCreate {
  sku_code: string;
  description: string;
  quantity_units: number;
  quantity_boxes: number;
  quantity_pieces: number;
  quantity_m2: number;
  quantity_blm: number;
  unit_price: number;
}

export interface QuoteCreate {
  company_id: string;
  store_type_id: number;
  customer_id: string | number;  // Allow both string and number
  deliver_to_address?: string;
  date: string; // Format: YYYY-MM-DD
  notes?: string;
  status?: string; // Add status field
  shipping?: number; // Add shipping field
  items: QuoteItemCreate[];
}

export interface Quote {
  id: string;
  quote_no: string;
  store_type: string;
  store_type_id?: number; // Add for editing support
  sale_type: string;
  customer_id: string;
  customer_name: string;
  company_id?: string; // Add for editing support
  deliver_to_address?: string;
  date: string;
  notes?: string;
  total_gst: number;
  grand_total: number;
  total_order: number;
  shipping: number;
  is_archived: boolean;
  status?: string; // Add status field
  created_at: string;
  updated_at: string;
  quote_items: QuoteItem[];
}

export interface QuotesResponse {
  items: Quote[];
  total: number;
  page: number;
  size: number;
}

export interface QuoteFilter {
  search?: string;
  start_date?: string;
  end_date?: string;
  page?: number;
  limit?: number;
  store_type?: string;
}

class QuoteService {
  private cache: Map<string, { data: any; timestamp: number }> = new Map();
  private readonly CACHE_DURATION = 30000; // 30 seconds cache

  constructor() {
    // Remove hardcoded baseURL - we'll use API_URL directly
  }

  private getCacheKey(url: string, params?: any): string {
    return `${url}_${JSON.stringify(params || {})}`;
  }

  private isValidCache(timestamp: number): boolean {
    return Date.now() - timestamp < this.CACHE_DURATION;
  }

  private async makeRequest<T>(url: string, options: RequestOptions = {}, useCache = false, bypassCache = false): Promise<T> {
    const cacheKey = this.getCacheKey(url, options.body);
    
    // Check cache for GET requests only if not bypassing cache
    if (useCache && !bypassCache && options.method === 'GET' && this.cache.has(cacheKey)) {
      const cached = this.cache.get(cacheKey)!;
      if (this.isValidCache(cached.timestamp)) {
        console.log('Using cached data for:', url);
        return cached.data;
      } else {
        this.cache.delete(cacheKey);
      }
    } else if (bypassCache && this.cache.has(cacheKey)) {
      // Clear cache if bypassing
      this.cache.delete(cacheKey);
    }

    // Pass bypassCache to apiRequest to also bypass apiClient cache
    const requestOptions = {
      ...options,
      bypassCache: bypassCache
    };

    const result = await apiRequest<T>(url, requestOptions);
    
    // Cache GET requests only if not bypassing cache
    if (useCache && !bypassCache && options.method === 'GET') {
      this.cache.set(cacheKey, { data: result, timestamp: Date.now() });
    }

    return result;
  }

  // Clear both internal cache and apiClient cache for quotes
  private clearCache(): void {
    this.cache.clear();
    // Clear apiClient cache for quote-related endpoints
    clearApiCache('/api/v1/quotes');
    console.log('Cleared both QuoteService internal cache and apiClient cache for quotes');
  }

  // Get current (non-archived) quotes with pagination and filtering
  getCurrentQuotes = async (filters: QuoteFilter = {}, storeFilter?: StoreFilterOptions, bypassCache: boolean = false): Promise<QuotesResponse> => {
    const { search, start_date, end_date, page = 1, limit = 10, store_type } = filters;

    let queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (start_date) queryParams.append('start_date', start_date);
    if (end_date) queryParams.append('end_date', end_date);
    if (store_type) queryParams.append('store_type', store_type);
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    // Apply store-based filtering for staff/manager users
    if (storeFilter) {
      queryParams = addStoreFilterToParams(queryParams, storeFilter);
    }

    const endpoint = `${ENDPOINTS.QUOTES_CURRENT}?${queryParams.toString()}`;
    return this.makeRequest<QuotesResponse>(endpoint, { method: 'GET' }, true, bypassCache); // Use cache unless bypassed
  };

  // Get archived quotes with pagination and filtering
  getArchivedQuotes = async (filters: QuoteFilter = {}, storeFilter?: StoreFilterOptions, bypassCache: boolean = false): Promise<QuotesResponse> => {
    const { search, start_date, end_date, page = 1, limit = 10, store_type } = filters;

    let queryParams = new URLSearchParams();
    if (search) queryParams.append('search', search);
    if (start_date) queryParams.append('start_date', start_date);
    if (end_date) queryParams.append('end_date', end_date);
    if (store_type) queryParams.append('store_type', store_type);
    queryParams.append('page', page.toString());
    queryParams.append('limit', limit.toString());

    // Apply store-based filtering for staff/manager users
    if (storeFilter) {
      queryParams = addStoreFilterToParams(queryParams, storeFilter);
    }

    const endpoint = `${ENDPOINTS.QUOTES_ARCHIVED}?${queryParams.toString()}`;
    return this.makeRequest<QuotesResponse>(endpoint, { method: 'GET' }, true, bypassCache); // Use cache unless bypassed
  };

  // Get a quote by ID
  getQuoteById = async (quoteId: string): Promise<Quote> => {
    return this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}`, { method: 'GET' }, true); // Use cache
  };

  // Create a new quote
  createQuote = async (quoteData: QuoteCreate): Promise<Quote> => {
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/`, { method: 'POST', body: JSON.stringify(quoteData) });
    this.clearCache(); // Clear cache after creating
    return result;
  };

  // Update an existing quote
  updateQuote = async (quoteId: string, quoteData: Partial<QuoteCreate> | any): Promise<Quote> => {
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}`, { method: 'PUT', body: JSON.stringify(quoteData) });
    this.clearCache(); // Clear cache after updating
    return result;
  };

  // Convert a quote to an invoice
  convertToInvoice = async (quoteId: string): Promise<Quote> => {
    console.log(`Attempting to convert quote ${quoteId} to invoice`);
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}/convert`, { method: 'PUT' });
    console.log(`Successfully converted quote to invoice:`, result);
    this.clearCache(); // Clear quote cache after converting
    // Also clear invoice cache since a new invoice was created
    clearApiCache('/api/v1/invoices');
    clearApiCache('/invoices');
    console.log('Cleared invoice cache after quote conversion');
    return result;
  };

  // Renew an archived quote
  renewQuote = async (quoteId: string): Promise<Quote> => {
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}/renew`, { method: 'PUT' });
    this.clearCache(); // Clear cache after renewing
    return result;
  };

  // Archive a quote
  archiveQuote = async (quoteId: string): Promise<Quote> => {
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}/archive`, { method: 'PUT' });
    this.clearCache(); // Clear cache after archiving
    return result;
  };

  // Unarchive a quote
  unarchiveQuote = async (quoteId: string): Promise<Quote> => {
    const result = await this.makeRequest<Quote>(`${ENDPOINTS.QUOTES}/${quoteId}/unarchive`, { method: 'PUT' });
    this.clearCache(); // Clear cache after unarchiving
    return result;
  };

  // Delete a quote
  deleteQuote = async (quoteId: string): Promise<void> => {
    const result = await this.makeRequest<void>(`${ENDPOINTS.QUOTES}/${quoteId}`, { method: 'DELETE' });
    this.clearCache(); // Clear cache after deleting
    return result;
  };

  // Format a date for API requests
  formatDate = (date: Date): string => {
    return format(date, 'yyyy-MM-dd');
  }
}

export default new QuoteService(); 